/**
 * Adds support to a per-workstation alternative URL to any resource action.
 *
 * Note that `TmrApi.endpoint` must be defined as object and have the
 * protocol and basePath properties. E.g:
 * `{ protocol: ':protocol', basePath: ':basePath', path: 'my/path' }`
 *
 * @example
 * let actions = {
 *   start: method: 'POST', url: TmrApi.endpoint('Antenna.start')
 * };
 * DeviceManager.processAction(actions.start);
 * TmrResource('Antenna', actions);
 *
 * @type {ngService}
 */
export class DeviceManager {
  static ngType = 'service'

  constructor($rootScope, tmrApiProtocol, tmrApiBasePath) {
    'ngInject'
    Object.assign(this, { tmrApiProtocol, tmrApiBasePath })
    $rootScope.$on('activeworkstationset', (event, workstation) =>
      this.onActiveWorkstationSet(workstation)
    )
  }

  /**
   * Processes a resource action by adding the local dynamic parameters protocol and basePath.
   *
   * @param action  A resource action
   */
  processAction(action) {
    if (action.url.indexOf(':protocol://:basePath') !== 0) {
      throw new Error(
        "DeviceManager actions must begin with :protocol://:basePath. Add { protocol: ':protocol', basePath: ':basePath' } to the endpoint object"
      )
    }
    action.params = action.params ?? {}
    action.params.protocol = () => this.getProtocol()
    action.params.basePath = () => this.getBasePath()
    action.timeout = action.timeout ?? 5000
    return action
  }

  /**
   * :protocol param callback.
   */
  getProtocol() {
    if (this.workstation !== undefined) {
      let urlParts = this.workstation.deviceManagerBasePath.split('://')
      // The protocol in deviceManagerBasePath is optional.
      if (urlParts.length == 2) return urlParts[0]
    }
    return this.tmrApiProtocol
  }

  /**
   * :basePath param callback.
   */
  getBasePath() {
    if (this.workstation !== undefined) {
      let urlParts = this.workstation.deviceManagerBasePath.split('://')
      // The protocol in deviceManagerBasePath is optional.
      return urlParts[1] ?? urlParts[0]
    }
    return this.tmrApiBasePath
  }

  /**
   * Modifies an endpoint url (protocol + base path) to make it point to the
   * custom device manager path defined in the active workstation.
   *
   * @param {object} endpoint The endpoint configuration object
   */
  processEndpoint(endpoint) {
    // The active workstation is loaded asynchronously, so we must just hope that it
    // was done loading before the resource invokes the processed actions.
    // Since the active workstation is needed for loading any page that uses devices,
    // this should always be the case.
    if (this.workstation === undefined) return

    if (['ws', 'wss'].indexOf(endpoint.protocol) > -1) {
      let urlParts = this.workstation.deviceManagerWebsocketPath.split('://')
      endpoint.protocol = urlParts[1] ? urlParts[0] : 'ws'
      endpoint.basePath = urlParts[1] ?? urlParts[0]
    } else {
      let urlParts = this.workstation.deviceManagerBasePath.split('://')
      endpoint.protocol = urlParts[1] ? urlParts[0] : 'http'
      endpoint.basePath = urlParts[1] ?? urlParts[0]
    }
    endpoint.prefix = ''
  }

  /**
   * Listens for active workstation updates and passes it to DeviceManager.
   */
  onActiveWorkstationSet(workstation) {
    this.workstation = workstation
  }
}
